/** Enable with `localStorage.setItem('tms_debug', '1')` then refresh (or use `window.__tmsDebug.enable()`). */
export const CONSOLE_DEBUG_STORAGE_KEY = 'tms_debug';

type ConsoleMethod =
  | 'log'
  | 'debug'
  | 'info'
  | 'warn'
  | 'error'
  | 'group'
  | 'groupCollapsed'
  | 'groupEnd'
  | 'table'
  | 'trace'
  | 'dir';

type NativeConsoleFn = (...args: any[]) => void;

const bindConsole = (fn: (...args: any[]) => void): NativeConsoleFn =>
  fn.bind(console) as NativeConsoleFn;

const nativeConsole: Record<ConsoleMethod, NativeConsoleFn> = {
  log: bindConsole(console.log),
  debug: bindConsole(console.debug),
  info: bindConsole(console.info),
  warn: bindConsole(console.warn),
  error: bindConsole(console.error),
  group: bindConsole(console.group),
  groupCollapsed: bindConsole(console.groupCollapsed),
  groupEnd: bindConsole(console.groupEnd),
  table: bindConsole(console.table),
  trace: bindConsole(console.trace),
  dir: bindConsole(console.dir),
};

export function isConsoleDebugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const value = localStorage.getItem(CONSOLE_DEBUG_STORAGE_KEY);
    return value === '1' || value === 'true';
  } catch {
    return false;
  }
}

export function setConsoleDebugEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (enabled) {
      localStorage.setItem(CONSOLE_DEBUG_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(CONSOLE_DEBUG_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode errors
  }
}

function wrapConsoleMethod(method: ConsoleMethod): NativeConsoleFn {
  return (...args: any[]) => {
    if (isConsoleDebugEnabled()) {
      nativeConsole[method](...args);
    }
  };
}

declare global {
  interface Window {
    __tmsConsoleGuardInstalled?: boolean;
    __tmsDebug?: {
      enable: () => void;
      disable: () => void;
      isEnabled: () => boolean;
    };
  }
}

/** Silence browser console output unless `tms_debug` is set in localStorage. */
export function installConsoleDebugGuard(): void {
  if (typeof window === 'undefined' || window.__tmsConsoleGuardInstalled) {
    return;
  }

  (Object.keys(nativeConsole) as ConsoleMethod[]).forEach((method) => {
    console[method] = wrapConsoleMethod(method);
  });

  window.__tmsConsoleGuardInstalled = true;
  window.__tmsDebug = {
    enable: () => {
      setConsoleDebugEnabled(true);
      nativeConsole.log('[tms] Console debug enabled');
    },
    disable: () => {
      setConsoleDebugEnabled(false);
      nativeConsole.log('[tms] Console debug disabled');
    },
    isEnabled: isConsoleDebugEnabled,
  };
}
